import { useEffect, useState } from 'react';
import {
  Modal,
  Paper,
  TextField,
  Grid,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Container } from 'types';
import CustomNumberInput from 'components/CustomNumberInput';
import { useSession } from 'providers/SessionProvider';
import {
  calculateContainerVolumeWeight,
  getUserFullName,
} from 'helpers';
import { isValidPositiveFloatNumber } from 'helpers/formatNumber';

type Props = {
  open: boolean;
  handleOpen: () => void;
  value: Container | undefined;
  onEdit: (container: Container) => void;
};

export default function EditContainerModal({
  open = true,
  handleOpen,
  value,
  onEdit,
}: Props) {
  const { users } = useSession();
  const buildContainerFormData = (container?: Container): Container => {
    const m3Min = container?.m3_min ?? 0;
    const m3Max = container?.m3_max ?? 0;

    return {
      id: container?.id ?? 0,
      description: container?.description ?? '',
      m3_min: m3Min,
      m3_max: m3Max,
      volume_weight:
        container?.volume_weight ?? calculateContainerVolumeWeight(m3Max),
      is_active: container?.is_active ?? false,
      created_at: container?.created_at,
      updated_at: container?.updated_at,
      created_by: container?.created_by,
    };
  };

  const [data, setData] = useState<Container>(buildContainerFormData(value));

  useEffect(() => {
    if (open) {
      setData(buildContainerFormData(value));
    }
  }, [open, value]);

  const handleChange = <K extends keyof Omit<Container, 'id'>>(
    name: K,
    value: Omit<Container, 'id'>[K]
  ) => {
    setData({
      ...data,
      [name]: value,
    });
  };

  const handleNumberChange = <K extends keyof Omit<Container, 'id'>>(
      name: K,
      rawValue: string
    ) => {
      setData(prev => ({
        ...prev,
        [name]: rawValue,
      }));
    };  

  const onSubmit = (updatedContainer: Container) => {
    onEdit({
      ...updatedContainer,
      volume_weight: calculateContainerVolumeWeight(updatedContainer.m3_max),
    });
    handleOpen();
  };

  const computedVolumeWeight = calculateContainerVolumeWeight(data.m3_max);

  return (
    <Modal open={open} onClose={handleOpen}>
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { sm: '90%', md: '30rem' },
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
        }}
      >
        <Grid container spacing={2}>
          <Typography variant='h5' gutterBottom>
            Editar Contenedor
          </Typography>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              required
              label='Descripción'
              fullWidth
              value={data.description}
              onChange={e => handleChange('description', e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <CustomNumberInput
              required
              label='M3 mínimo'
              value={data.m3_min}
              allowOnlyPositive
              onChange={e => handleNumberChange('m3_min', e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <CustomNumberInput
              required
              label='M3 máximo'
              value={data.m3_max}
              allowOnlyPositive
              onChange={e => handleNumberChange('m3_max', e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              disabled
              fullWidth
              label='Peso volumétrico'
              value={computedVolumeWeight}
              helperText='Calculado automáticamente: m3_max × 350'
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={data.is_active}
                  onChange={(e) => handleChange("is_active", e.target.checked)}
                  color="primary"
                />
              }
              label="Contenedor activo"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              disabled
              fullWidth
              label='Creado por'
              value={
                data.created_by
                  ? getUserFullName(data.created_by, users)
                  : 'SISTEMA'
              }
            />
          </Grid>
          <Grid item xs={12} display='flex' justifyContent='end' gap={1}>
            <Button
              type='button'
              variant='outlined'
              onClick={() => {
                handleOpen();
              }}
            >
              Cancelar
            </Button>
            <Button
              type='submit'
              variant='contained'
              disabled={
                !isValidPositiveFloatNumber(data.m3_min?.toString() || '') ||
                !isValidPositiveFloatNumber(data.m3_max?.toString() || '') ||
                Number(data.m3_max) < Number(data.m3_min)
              }
              onClick={() => {
                onSubmit(data);
              }}
            >
              Guardar
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
}
